import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { formService } from '../../../services/formServices';

import './Modal.scss';

const JobApplyModal = ({ onClose, jobId, jobPosition = "Embedded Engineer" }) => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        message: '',
        resumeUrl: '',
        job: jobId || ''
    });

    const [resumeFile, setResumeFile] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadingFile, setUploadingFile] = useState(false);
    const [uploadError, setUploadError] = useState('');

    const handleInputChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Reset upload error
        setUploadError('');

        // Validate file
        const validTypes = [
            'application/pdf',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'application/vnd.ms-word'
        ];

        const validExtensions = ['.pdf', '.doc', '.docx'];
        const maxSize = 5 * 1024 * 1024; // 5MB

        // Check file type
        const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
        if (!validTypes.includes(file.type) && !validExtensions.includes(fileExtension)) {
            setUploadError('Please upload a PDF, DOC, or DOCX file');
            return;
        }

        if (file.size > maxSize) {
            setUploadError('File size must be less than 5MB');
            return;
        }

        setResumeFile(file);
        setUploadingFile(true);
        setUploadProgress(0);

        try {
            // Create FormData for file upload
            const formData = new FormData();
            formData.append('file', file);

            // Upload file using the service
            const response = await formService.uploadResume(file);
            console.log(response)


            if (response && response?.fileUrl) {
                setFormData(prev => ({
                    ...prev,
                    resumeUrl: response?.fileUrl
                }));
                setUploadProgress(100);

                // Clear error on successful upload
                setUploadError('');
            } else {
                throw new Error('No URL returned from upload');
            }
        } catch (error) {
            console.error('File upload error:', error);
            setUploadError(
                error.response?.data?.message ||
                error.message ||
                'Failed to upload resume. Please try again.'
            );
            setResumeFile(null);
            setFormData(prev => ({ ...prev, resumeUrl: '' }));
        } finally {
            // Small delay to show 100% completion
            setTimeout(() => {
                setUploadingFile(false);
                setUploadProgress(0);
            }, 500);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate required fields
        if (!jobId) {
            alert('Job ID is required');
            return;
        }

        if (!resumeFile || !formData.resumeUrl) {
            alert('Please upload your resume');
            return;
        }

        if (!formData.name || !formData.email || !formData.phone) {
            alert('Please fill in all required fields');
            return;
        }

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            alert('Please enter a valid email address');
            return;
        }

        setIsSubmitting(true);

        try {
            // Prepare data for submission
            const submissionData = {
                name: formData.name,
                email: formData.email,
                phone: formData.phone,
                message: formData.message,
                resumeUrl: formData.resumeUrl,
                job: jobId,
                jobRole: jobPosition
            };

            // Submit application
            await formService.submitCareerApplication(submissionData);

            // Success
            alert("Application submitted successfully!");

            // Reset form
            setFormData({
                name: '',
                email: '',
                phone: '',
                message: '',
                resumeUrl: '',
                job: jobId
            });
            setResumeFile(null);
            setUploadError('');

            onClose();
        } catch (error) {
            console.error('Application submission error:', error);
            alert(error.response?.data?.message || "Failed to submit application. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    // Remove file handler
    const handleRemoveFile = () => {
        setResumeFile(null);
        setFormData(prev => ({ ...prev, resumeUrl: '' }));
        setUploadError('');
    };

    return (
        <div className="contact-modal">
            <div className="modal-grid">
                {/* Left Column */}
                <div className="modal-left">
                    <motion.span
                        className="support-label"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1 }}
                    >
                        CAREER OPPORTUNITY
                    </motion.span>

                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                    >
                        Join Our Team
                    </motion.h2>

                    <motion.p
                        className="subheading"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                    >
                        {jobPosition ? `Applying for: ${jobPosition}` : "We're excited to review your application!"}
                    </motion.p>

                    <motion.div
                        className="application-tips"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                    >
                        <h4>Application Requirements:</h4>
                        <ul>
                            <li>✓ Resume (PDF, DOC, DOCX) - Max 5MB</li>
                            <li>✓ Valid email address</li>
                            <li>✓ Phone number for contact</li>
                        </ul>
                    </motion.div>
                </div>

                {/* Right Column - Form */}
                <motion.div
                    className="modal-right"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 }}
                >
                    <form onSubmit={handleSubmit} className="contact-form">
                        <div className="form-row">
                            <div className="form-group">
                                <input
                                    type="text"
                                    name="name"
                                    placeholder="Full Name *"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    required
                                    className="form-input"
                                    disabled={isSubmitting || uploadingFile}
                                />
                            </div>

                            <div className="form-group">
                                <input
                                    type="email"
                                    name="email"
                                    placeholder="Email Address *"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    required
                                    className="form-input"
                                    disabled={isSubmitting || uploadingFile}
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <input
                                type="tel"
                                name="phone"
                                placeholder="Phone Number *"
                                value={formData.phone}
                                onChange={handleInputChange}
                                required
                                className="form-input"
                                disabled={isSubmitting || uploadingFile}
                            />
                        </div>

                        <div className="form-group">
                            <label className="file-input-label">
                                Upload Resume (PDF, DOC, DOCX) *
                                {uploadingFile && (
                                    <span className="upload-progress">
                                        Uploading... {uploadProgress}%
                                    </span>
                                )}
                            </label>
                            <input
                                type="file"
                                onChange={handleFileChange}
                                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                required
                                className="form-input"
                                disabled={isSubmitting || uploadingFile}
                                style={uploadingFile ? { opacity: 0.5 } : {}}
                            />

                            {/* Progress bar */}
                            {uploadingFile && (
                                <div className="progress-bar-container">
                                    <div
                                        className="progress-bar"
                                        style={{ width: `${uploadProgress}%` }}
                                    />
                                </div>
                            )}

                            {/* File info and remove button */}
                            {resumeFile && !uploadingFile && (
                                <div className="file-info-container">
                                    <div className="file-info">
                                        <span className="file-name">
                                            {resumeFile.name}
                                        </span>
                                        <span className="file-size">
                                            {(resumeFile.size / 1024 / 1024).toFixed(2)} MB
                                        </span>
                                    </div>
                                    <button
                                        type="button"
                                        className="remove-file-btn"
                                        onClick={handleRemoveFile}
                                        disabled={isSubmitting}
                                    >
                                        Remove
                                    </button>
                                </div>
                            )}

                            {/* Upload error */}
                            {uploadError && (
                                <div className="upload-error">
                                    {uploadError}
                                </div>
                            )}
                        </div>

                        <div className="form-group">
                            <textarea
                                name="message"
                                placeholder="Additional Message (Optional)"
                                rows="3"
                                value={formData.message}
                                onChange={handleInputChange}
                                className="form-textarea"
                                disabled={isSubmitting || uploadingFile}
                            />
                        </div>

                        <motion.button
                            type="submit"
                            className="send-btn"
                            disabled={isSubmitting || uploadingFile || !resumeFile || !formData.resumeUrl}
                            whileHover={(!isSubmitting && !uploadingFile && resumeFile && formData.resumeUrl) ? { scale: 1.02 } : {}}
                            whileTap={(!isSubmitting && !uploadingFile && resumeFile && formData.resumeUrl) ? { scale: 0.98 } : {}}
                        >
                            {isSubmitting ? 'Submitting...' :
                                uploadingFile ? 'Uploading...' :
                                    'Submit Application'}
                        </motion.button>
                    </form>
                </motion.div>
            </div>
        </div>
    );
};

export default JobApplyModal;